import React, { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { Announcement } from '../types';
import PublicHeader from './PublicHeader';
import StorageImage from './StorageImage';
import AnnouncementAttachment from './AnnouncementAttachment';
import { formatClubDate } from '../utils/clubTime';

const client = generateClient();

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return formatClubDate(date, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

interface Props {
  announcementId: string;
  onNavigateHome: () => void;
  onNavigateToAnnouncements: () => void;
}

/**
 * Full single-announcement detail view, reachable via /announcements/:id.
 * Renders the complete, untruncated content (unlike the landing page's
 * spotlight card and the archive grid, which both show a short excerpt).
 */
const AnnouncementDetailPage: React.FC<Props> = ({
  announcementId,
  onNavigateHome,
  onNavigateToAnnouncements,
}) => {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setNotFound(false);
        const result = await client.models.Announcement.get({ id: announcementId });
        if (cancelled) return;
        if (!result.data || !result.data.isActive) {
          setNotFound(true);
        } else {
          setAnnouncement(result.data as Announcement);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading announcement:', err);
        setError('Failed to load this announcement. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [announcementId]);

  return (
    <main className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <PublicHeader title="Announcements" />

      <div className="max-w-3xl mx-auto pt-6 px-4 flex items-center justify-between gap-4">
        <button
          onClick={onNavigateToAnnouncements}
          className="text-sm text-fishing-blue hover:underline"
        >
          ← All Announcements
        </button>
        <button
          onClick={onNavigateHome}
          className="text-sm text-fishing-blue hover:underline"
        >
          Back to Home
        </button>
      </div>

      <div className="max-w-3xl mx-auto py-12 px-4">
        {loading ? (
          <div className="text-center py-20 text-gray-600">Loading…</div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">{error}</div>
        ) : notFound ? (
          <div className="text-center py-20">
            <p className="text-gray-600 mb-4">
              This announcement could not be found. It may have been removed or unpublished.
            </p>
            <div className="flex items-center justify-center gap-6">
              <button
                onClick={onNavigateToAnnouncements}
                className="text-fishing-blue font-medium hover:underline"
              >
                ← Back to all announcements
              </button>
              <button
                onClick={onNavigateHome}
                className="text-fishing-blue font-medium hover:underline"
              >
                Back to Home
              </button>
            </div>
          </div>
        ) : announcement ? (
          <article className="bg-white rounded-lg shadow-md overflow-hidden">
            {announcement.imageUrl && (
              <StorageImage
                storageKey={announcement.imageUrl}
                alt={announcement.title}
                className="w-full max-h-[420px] object-cover"
              />
            )}
            <div className="p-6 md:p-10">
              <div className="flex items-center gap-2 mb-4">
                {announcement.isPinned && (
                  <span className="bg-fishing-green text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide">
                    Pinned
                  </span>
                )}
                <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                  {announcement.category?.replace('_', ' ')}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-fishing-blue mb-3">
                {announcement.title}
              </h1>
              <p className="text-sm text-gray-500 mb-8">{formatDate(announcement.publishDate)}</p>
              <div className="text-gray-800 text-lg leading-relaxed whitespace-pre-line">
                {announcement.content}
              </div>
              <AnnouncementAttachment
                tags={(announcement as { tags?: (string | null)[] }).tags}
                label={
                  /wrap[- ]?up/i.test(announcement.title || '')
                    ? 'Open the full wrap-up (PDF)'
                    : 'Open the attached PDF'
                }
              />
            </div>
          </article>
        ) : null}
      </div>
    </main>
  );
};

export default AnnouncementDetailPage;
